/**
 * Posture — the landing page. Answers "are we safe right now?" in one screen.
 */
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { AlertsTrend, TacticBars } from "@/components/security/SecurityCharts";
import {
  useEdrAlerts,
  useEdrHealthInsights,
  useEdrCoverageCatalog,
  useEdrScorecard,
} from "@/hooks/security/useEdrData";
import { computeFleetHealth } from "@/lib/fleetHealth";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { formatDistanceToNow } from "date-fns";


export default function PosturePage() {
  return (
    <ConnectorGate connectorId="edr">
      <PostureInner />
    </ConnectorGate>
  );
}

function PostureInner() {
  const alertsQ = useEdrAlerts({ limit: 500 });
  const healthQ = useEdrHealthInsights();
  const catalogQ = useEdrCoverageCatalog();
  const scorecardQ = useEdrScorecard();

  const alerts = alertsQ.data ?? [];
  const open = alerts.filter((a) => a.status === "new");
  const critical = open.filter((a) => a.severity === "critical").length;
  const high = open.filter((a) => a.severity === "high").length;
  const health = healthQ.data ?? {};
  const fleet = computeFleetHealth(health.sensorsHealthy, health.sensorsStale);
  const { healthy, stale, total, hasSensors } = fleet;

  const detections = catalogQ.data?.detections ?? [];
  const enabledRules = detections.length;
  const tacticActivity = aggregateTactics(alerts);

  const acked = alerts.filter((a) => a.status !== "new");
  const mttaMins = meanMinutes(acked);

  const scorecard = scorecardQ.data;
  const headline = scorecard?.headline ?? {};
  const scorecardLoading = scorecardQ.isLoading;
  const scorecardOk = !scorecardQ.isError;

  const detectionsHint = scorecardLoading
    ? "—"
    : typeof headline.mitreTactics === "number" && typeof headline.mitreTechniques === "number"
      ? `${headline.mitreTactics} tactics · ${headline.mitreTechniques} techniques`
      : `${tacticActivity.length} tactic${tacticActivity.length === 1 ? "" : "s"} active`;

  const summary = buildSummary({
    open: open.length,
    critical,
    healthy,
    total,
    enabledRules,
  });

  // Surface-level error if the primary alerts query fails (everything else is
  // additive context).
  const shellError = alertsQ.isError ? alertsQ.error : undefined;
  const onShellRetry = alertsQ.isError ? () => alertsQ.refetch() : undefined;

  return (
    <SurfaceShell
      overline="Security · Posture"
      title="Are we safe right now?"
      summary={summary}
      error={shellError}
      onRetry={onShellRetry}
      actions={
        <Button asChild size="sm">
          <Link to="/triage">
            Go to Triage <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
      kpis={
        <>
          <StatTile
            label="Open incidents"
            value={open.length}
            loading={alertsQ.isLoading || alertsQ.isError}
            tone={critical > 0 ? "danger" : open.length > 0 ? "warning" : "success"}
            hint={`${critical} critical · ${high} high`}
          />
          <StatTile
            label="MTTA"
            value={mttaMins ? `${mttaMins}m` : "—"}
            loading={alertsQ.isLoading || alertsQ.isError}
            hint="Mean time to acknowledge"
          />
          {hasSensors ? (
            <StatTile
              label="Fleet healthy"
              value={fleet.value}
              loading={healthQ.isLoading || healthQ.isError}
              tone={fleet.tone}
              hint={`${healthy} of ${total} sensors reporting`}
            />
          ) : (
            <StatTile
              label="Fleet healthy"
              value={healthQ.isLoading || healthQ.isError ? "—" : "Awaiting sensor"}
              loading={healthQ.isLoading || healthQ.isError}
              tone="default"
              hint="Mint a sensor enrollment code to begin"
            />
          )}
          <StatTile
            label="Detections active"
            value={enabledRules || "—"}
            loading={catalogQ.isLoading || catalogQ.isError}
            hint={detectionsHint}
          />
        </>
      }
    >
      {scorecardOk && (
        <SmplifyScoreStrip headline={headline} loading={scorecardLoading} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <Panel
          title="Alert volume · last 24h"
          subtitle="Stacked by severity"
          loading={alertsQ.isLoading}
          error={alertsQ.error}
          isError={alertsQ.isError}
          onRetry={() => alertsQ.refetch()}
          empty={!alertsQ.isLoading && !alertsQ.isError && alerts.length === 0}
          emptyTitle="No alerts in window"
          emptyDescription="Nothing has fired in the last 24 hours."
          skeleton="card"
        >
          <AlertsTrend alerts={alerts} loading={false} />
        </Panel>
        <Panel
          title="MITRE tactic activity"
          subtitle="Where attackers are touching"
          loading={alertsQ.isLoading}
          error={alertsQ.error}
          isError={alertsQ.isError}
          onRetry={() => alertsQ.refetch()}
          empty={!alertsQ.isLoading && !alertsQ.isError && tacticActivity.length === 0}
          emptyTitle="No tactic activity"
          emptyDescription="Alerts have no MITRE mapping yet."
          skeleton="card"
        >
          <TacticBars data={tacticActivity} loading={false} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Panel
          title="What changed in 24h"
          loading={alertsQ.isLoading || healthQ.isLoading}
          skeleton="list"
        >
          <ChangedFeed alerts={alerts} healthy={healthy} stale={stale} />
        </Panel>
        <Panel
          title="Top critical alerts"
          loading={alertsQ.isLoading}
          empty={!alertsQ.isLoading && open.length === 0}
          emptyTitle="All clear"
          emptyDescription="No critical alerts open right now."
          skeleton="list"
          actions={
            <Link to="/triage" className="text-[11px] text-primary hover:underline">
              Open queue
            </Link>
          }
        >
          <TopAlerts alerts={open.slice(0, 5)} />
        </Panel>
      </div>
    </SurfaceShell>
  );
}

function SmplifyScoreStrip({
  headline,
  loading,
}: {
  headline: { [k: string]: unknown };
  loading: boolean;
}) {
  const dash = "—";
  const num = (k: string) => {
    if (loading) return dash;
    const v = headline[k];
    return typeof v === "number" ? v.toLocaleString() : dash;
  };
  const segments: Array<{ value: string; label: string }> = [
    { value: num("detectionsShipping"), label: "detections" },
    { value: num("mitreTactics"), label: "ATT&CK tactics" },
    { value: num("mitreTechniques"), label: "techniques" },
    { value: num("frameworksMapped"), label: "frameworks" },
    { value: num("aiAgentsCatalog"), label: "AI agents tracked" },
  ];
  return (
    <div className="mb-4 -mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
        Smplify Score
      </span>
      {segments.map((seg, i) => (
        <span key={seg.label} className="flex items-baseline gap-1.5">
          {i > 0 && <span className="text-muted-foreground/40">·</span>}
          <Link
            to="/coverage"
            className="font-medium text-foreground tabular-nums hover:text-primary transition-colors"
          >
            {seg.value}
          </Link>
          <span>{seg.label}</span>
        </span>
      ))}
    </div>
  );
}

function buildSummary({ open, critical, healthy, total, enabledRules }: {
  open: number; critical: number; healthy: number; total: number; enabledRules: number;
}) {
  const pieces: string[] = [];
  if (critical > 0) pieces.push(`${critical} critical incident${critical === 1 ? "" : "s"} need attention.`);
  else if (open > 0) pieces.push(`${open} alert${open === 1 ? "" : "s"} waiting.`);
  else pieces.push("No active incidents.");
  if (total > 0) pieces.push(`${healthy} of ${total} sensors healthy.`);
  if (enabledRules > 0) pieces.push(`${enabledRules} detections protecting the fleet.`);
  return pieces.join(" ");
}

function aggregateTactics(alerts: { mitre: string }[]) {
  // Approximate tactic from technique prefix until catalog mapping is available.
  const TACTIC_BY_ID: Record<string, string> = {
    T1059: "Execution",
    T1071: "Command & Control",
    T1003: "Credential Access",
    T1547: "Persistence",
    T1486: "Impact",
    T1027: "Defense Evasion",
    T1555: "Credential Access",
    T1078: "Initial Access",
    T1110: "Credential Access",
    T1041: "Exfiltration",
  };
  const counts = new Map<string, number>();
  for (const a of alerts) {
    const key = a.mitre?.split(".")[0] ?? "";
    const tactic = TACTIC_BY_ID[key] ?? (a.mitre && a.mitre !== "—" ? "Other" : null);
    if (!tactic) continue;
    counts.set(tactic, (counts.get(tactic) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([tactic, count]) => ({ tactic, count }))
    .sort((a, b) => b.count - a.count);
}

function meanMinutes(items: { ts: string }[]) {
  if (items.length === 0) return 0;
  const now = Date.now();
  const ages = items.map((i) => Math.max(0, (now - new Date(i.ts).getTime()) / 60000));
  return Math.round(ages.reduce((a, b) => a + b, 0) / ages.length);
}

function ChangedFeed({ alerts, healthy, stale }: { alerts: any[]; healthy: number; stale: number }) {
  const last24h = alerts.filter((a) => Date.now() - new Date(a.ts).getTime() < 86400000);
  const items = [
    last24h.length > 0 && {
      icon: ShieldAlert,
      tone: "warning" as const,
      text: `${last24h.length} new alerts in last 24h`,
    },
    stale > 0 && {
      icon: Activity,
      tone: "warning" as const,
      text: `${stale} sensors are stale (no check-in in 1h+)`,
    },
    healthy > 0 && stale === 0 && {
      icon: Activity,
      tone: "success" as const,
      text: `All ${healthy} sensors checking in`,
    },
  ].filter(Boolean) as Array<{ icon: any; tone: "warning" | "success"; text: string }>;

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No notable changes.</p>;
  }
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm">
          <it.icon className={`h-4 w-4 mt-0.5 flex-none ${
            it.tone === "warning" ? "text-warning" : "text-success"
          }`} />
          <span className="text-foreground">{it.text}</span>
        </li>
      ))}
    </ul>
  );
}

function TopAlerts({ alerts }: { alerts: any[] }) {
  if (alerts.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No critical alerts.</p>;
  }
  return (
    <ul className="divide-y divide-border -mx-4 -my-4">
      {alerts.map((a) => (
        <li key={a.id} className="px-4 py-2.5 hover:bg-muted/40">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2">
              <span className={`h-1.5 w-1.5 rounded-full flex-none ${
                a.severity === "critical" ? "bg-destructive" :
                a.severity === "high" ? "bg-destructive/70" : "bg-warning"
              }`} />
              <span className="text-sm font-medium text-foreground truncate">{a.ruleName}</span>
            </div>
            <span className="text-[11px] text-muted-foreground tabular-nums flex-none">
              {formatDistanceToNow(new Date(a.ts), { addSuffix: true })}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
            {a.deviceName} · {a.mitre}
          </div>
        </li>
      ))}
    </ul>
  );
}
